import React from 'react';
import PropTypes from 'prop-types';
import { Box, Grid } from 'grommet';
import { MetricCard } from '@Components/Partial/Product/Analytics';
import {
  StyledPanel,
  StyledScrollWrap,
  StyledTable,
} from './StyledCidAnalyticsDashboardPage';
import {
  PERF_COLORS,
  formatViews, ytVideoUrl,
  PanelTitle, EmptyTable,
} from './CidAnalyticsShared';
import { RpmByMonthChart } from './CidAnalyticsCharts';

export default function CidAnalyticsPerformanceTab({
  viewsAnalytics,
  claimsPerformance,
  loading,
  page,
  small,
}) {
  return (
    <Box gap="1rem">

      {/* Performance KPI cards */}
      <Grid columns={small ? ['1fr', '1fr'] : ['1fr', '1fr', '1fr', '1fr']} gap="1rem">
        <MetricCard
          label="OVERALL RPM"
          value={viewsAnalytics ? `$${viewsAnalytics.overallRpm ?? 0}` : 'Data unavailable'}
          subLabel="estimated revenue per 1,000 engaged views"
          textColor={PERF_COLORS.rpm}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={PERF_COLORS.rpm}
        />
        <MetricCard
          label="ZERO-VIEW ASSETS"
          value={viewsAnalytics ? `${viewsAnalytics.zeroViewPct ?? 0}%` : 'Data unavailable'}
          subLabel={viewsAnalytics ? `${viewsAnalytics.zeroViewAssetCount} of ${viewsAnalytics.totalActiveAssetCount} active assets with no views` : 'claimed assets with no engaged views'}
          textColor={PERF_COLORS.zeroView}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={PERF_COLORS.zeroView}
        />
        <MetricCard
          label="LOW LIFETIME CLAIMS"
          value={claimsPerformance ? String(claimsPerformance.lifetimeLow?.count ?? 0) : 'Data unavailable'}
          subLabel="active claims with under 50k lifetime views"
          textColor={PERF_COLORS.low}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={PERF_COLORS.low}
        />
        <MetricCard
          label="HIGH LIFETIME CLAIMS"
          value={claimsPerformance ? String(claimsPerformance.lifetimeHigh?.count ?? 0) : 'Data unavailable'}
          subLabel="active claims with 50k+ lifetime views"
          textColor={PERF_COLORS.high}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={PERF_COLORS.high}
        />
      </Grid>

      <Grid columns={small ? ['1fr', '1fr'] : ['1fr', '1fr']} gap="1rem">
        <MetricCard
          label="LOW DAILY CLAIMS"
          value={claimsPerformance ? String(claimsPerformance.dailyLow?.count ?? 0) : 'Data unavailable'}
          subLabel="avg under 2k daily views — last 30 days"
          textColor={PERF_COLORS.low}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={PERF_COLORS.low}
        />
        <MetricCard
          label="HIGH DAILY CLAIMS"
          value={claimsPerformance ? String(claimsPerformance.dailyHigh?.count ?? 0) : 'Data unavailable'}
          subLabel="avg 2k+ daily views — last 30 days"
          textColor={PERF_COLORS.high}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={PERF_COLORS.high}
        />
      </Grid>

      {/* RPM trend chart */}
      <StyledPanel gap="0.75rem">
        <PanelTitle label="Estimated RPM by Month" />
        <RpmByMonthChart data={viewsAnalytics?.rpmByMonth ?? null} loading={loading} />
      </StyledPanel>

      {/* Two-column: lifetime claim tables */}
      <Grid columns={small ? ['100%'] : ['1fr', '1fr']} gap="1rem">
        <StyledPanel gap="0.75rem">
          <PanelTitle label="Low Lifetime Views (< 50k)" />
          {claimsPerformance?.lifetimeLow?.items?.length > 0 ? (
            <StyledScrollWrap>
              <StyledTable>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Video ID</th>
                    <th style={{ textAlign: 'left' }}>Asset Label</th>
                    <th>Lifetime Views</th>
                  </tr>
                </thead>
                <tbody>
                  {claimsPerformance.lifetimeLow.items.slice(0, 100).map((row) => (
                    <tr key={row.videoId}>
                      <td style={{ textAlign: 'left', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        <a href={ytVideoUrl(row.videoId)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--page-link)' }}>{row.videoId}</a>
                      </td>
                      <td style={{ textAlign: 'left' }}>{row.assetLabel || '—'}</td>
                      <td style={{ color: PERF_COLORS.low }}>{formatViews(row.lifetimeViews)}</td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </StyledScrollWrap>
          ) : <EmptyTable page={page} loading={loading} />}
        </StyledPanel>

        <StyledPanel gap="0.75rem">
          <PanelTitle label="High Lifetime Views (≥ 50k)" />
          {claimsPerformance?.lifetimeHigh?.items?.length > 0 ? (
            <StyledScrollWrap>
              <StyledTable>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Video ID</th>
                    <th style={{ textAlign: 'left' }}>Asset Label</th>
                    <th>Lifetime Views</th>
                  </tr>
                </thead>
                <tbody>
                  {claimsPerformance.lifetimeHigh.items.slice(0, 100).map((row) => (
                    <tr key={row.videoId}>
                      <td style={{ textAlign: 'left', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        <a href={ytVideoUrl(row.videoId)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--page-link)' }}>{row.videoId}</a>
                      </td>
                      <td style={{ textAlign: 'left' }}>{row.assetLabel || '—'}</td>
                      <td style={{ color: PERF_COLORS.high, fontWeight: 600 }}>{formatViews(row.lifetimeViews)}</td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </StyledScrollWrap>
          ) : <EmptyTable page={page} loading={loading} />}
        </StyledPanel>
      </Grid>

      {/* Two-column: daily avg claim tables */}
      <Grid columns={small ? ['100%'] : ['1fr', '1fr']} gap="1rem">
        <StyledPanel gap="0.75rem">
          <PanelTitle label="Low Daily Avg Views (< 2k, 30d)" />
          {claimsPerformance?.dailyLow?.items?.length > 0 ? (
            <StyledScrollWrap>
              <StyledTable>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Video ID</th>
                    <th style={{ textAlign: 'left' }}>Asset Label</th>
                    <th>Avg Daily Views</th>
                  </tr>
                </thead>
                <tbody>
                  {claimsPerformance.dailyLow.items.slice(0, 100).map((row) => (
                    <tr key={row.videoId}>
                      <td style={{ textAlign: 'left', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        <a href={ytVideoUrl(row.videoId)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--page-link)' }}>{row.videoId}</a>
                      </td>
                      <td style={{ textAlign: 'left' }}>{row.assetLabel || '—'}</td>
                      <td style={{ color: PERF_COLORS.low }}>{formatViews(row.avgDailyViews)}</td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </StyledScrollWrap>
          ) : <EmptyTable page={page} loading={loading} />}
        </StyledPanel>

        <StyledPanel gap="0.75rem">
          <PanelTitle label="High Daily Avg Views (≥ 2k, 30d)" />
          {claimsPerformance?.dailyHigh?.items?.length > 0 ? (
            <StyledScrollWrap>
              <StyledTable>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Video ID</th>
                    <th style={{ textAlign: 'left' }}>Asset Label</th>
                    <th>Avg Daily Views</th>
                  </tr>
                </thead>
                <tbody>
                  {claimsPerformance.dailyHigh.items.slice(0, 100).map((row) => (
                    <tr key={row.videoId}>
                      <td style={{ textAlign: 'left', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        <a href={ytVideoUrl(row.videoId)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--page-link)' }}>{row.videoId}</a>
                      </td>
                      <td style={{ textAlign: 'left' }}>{row.assetLabel || '—'}</td>
                      <td style={{ color: PERF_COLORS.high, fontWeight: 600 }}>{formatViews(row.avgDailyViews)}</td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </StyledScrollWrap>
          ) : <EmptyTable page={page} loading={loading} />}
        </StyledPanel>
      </Grid>

    </Box>
  );
}

CidAnalyticsPerformanceTab.propTypes = {
  viewsAnalytics: PropTypes.shape({
    overallRpm: PropTypes.number,
    zeroViewPct: PropTypes.number,
    zeroViewAssetCount: PropTypes.number,
    totalActiveAssetCount: PropTypes.number,
    rpmByMonth: PropTypes.array,
  }),
  claimsPerformance: PropTypes.shape({
    lifetimeLow: PropTypes.shape({ count: PropTypes.number, items: PropTypes.array }),
    lifetimeHigh: PropTypes.shape({ count: PropTypes.number, items: PropTypes.array }),
    dailyLow: PropTypes.shape({ count: PropTypes.number, items: PropTypes.array }),
    dailyHigh: PropTypes.shape({ count: PropTypes.number, items: PropTypes.array }),
  }),
  loading: PropTypes.bool.isRequired,
  page: PropTypes.object.isRequired,
  small: PropTypes.bool.isRequired,
};
